<template lang="pug">
  v-container.px-10(fluid)
    v-progress-linear(
      :active="loading",
      indeterminate,
      color="white",
      absolute,
      top
    )
    v-row.pb-10.pt-5.align-self-start(align="center", v-if="entity")
      v-col.py-0.text-right
        v-btn(color="white", @click="save()", :disabled="uploading") {{ $t('Common.save') }}
    v-row(v-if="!loading")
      v-col.py-0
        v-card.elevation-24.mb-5
          app-full-entity(:entity="entity", :instance="instance")
            template(v-slot:ReferenceMatrix="{ _instance, _entity, _errors, _relation, _options, _parent }")
              v-container
                v-row
                  v-col(cols="12", md="6")
                    h2.primary--text {{ $t('ReferenceMatrix.generalInfo') }}
                v-row
                  v-col(cols="3", xs="12")
                    v-text-field(
                      v-model="instance.name",
                      :label="$t('ReferenceMatrix.name')",
                      :error-messages="errors['Name']",
                    )
                  v-col(cols="6", xs="12")
                    v-text-field(
                      v-model="instance.description",
                      :label="$t('ReferenceMatrix.description')",
                      :error-messages="errors['Description']",
                    )
                  v-col(cols="3", xs="12")
                    v-select(
                      :label="$t('ReferenceMatrix.entity')",
                      v-model="instance.entity",
                      :items="$store.state.schema.all",
                      item-text="name",
                      item-value="name",
                      @input="getExtraFields($event)"
                     )
                v-row
                  v-col(cols="4", xs="12")
                    app-datetime-picker(
                      :value="instance.createdAt",
                      :label="$t('ReferenceMatrix.createdAt')",
                      disabled,
                    )
                  v-col(cols="4", xs="12")
                    app-datetime-picker(
                      :value="instance.modifiedAt",
                      :label="$t('ReferenceMatrix.modifiedAt')",
                      disabled,
                    )
                  v-col(cols="4", xs="12")
                    v-text-field(
                      :label="$t('ReferenceMatrix.userFullName')",
                      :value="(instance.user || {}).fullName"
                      disabled,
                    )
                v-row
                  v-col(cols="12", md="6")
                    h2.primary--text {{ $t('ReferenceMatrix.headersAndContent') }}
                v-row
                  v-col(cols="3")
                    v-select(
                      v-model="instance.dataTypeContent",
                      :label="$t('ReferenceMatrix.dataTypeContent')",
                      :items="['String', 'DateTime', 'Int', 'Float']",
                      :error-messages="errors['DataTypeContent']",
                    )
                    br
                    h2.primary--text {{ $t('ReferenceMatrix.file') }}
                    v-file-input(
                      v-model="instance.file",
                      :label="$t('ReferenceMatrix.uploadFile')",
                      :error-messages="errors['File']",
                      show-size,
                      accept=".xlsx, .xls, .csv",
                    )
                    p.uploading(v-if="uploading") {{ $t('ReferenceMatrix.uploadingFile') }}
                    v-progress-linear(rounded, color="secondary", indeterminate, height="6", :active="uploading")
                    reference-matrix-file(:localFiles="filesToUpload", :localForm="filesFormData", :entity="entity", :parentInstance="instance")
                  v-col(cols="3")
                    template(v-if="instance.dataTypeContent === 'DateTime'")
                      app-datetime-picker(v-model="instance.defaultValue" :label="$t('ReferenceMatrix.defaultValue')" :error-messages="errors['Default value']")
                    template(v-else)
                      v-text-field(v-model="instance.defaultValue" :label="$t('ReferenceMatrix.defaultValue')" :error-messages="errors['Default value']")
                  v-col.py-0(cols="9")
                    v-row
                      v-col.align-right(cols="12")
                        g-btn.h8.btn-headerX(
                          @click="addHeaderX()"
                        ) {{ $t('ReferenceMatrix.addHeader', { axis: 'X' }) }}
                        g-btn.h8.btn-headerY(
                          @click="addHeaderY()"
                        ) {{ $t('ReferenceMatrix.addHeader', { axis: 'Y' }) }}
                    v-row
                      v-container.grid-container
                        v-row.flex-nowrap(v-for="(headerX, index) in headersX", dense, :key="headerX.path")
                          v-spacer
                          v-col(cols="auto", v-for="(headerY, index) in headersY", :key="index + 'holder'")
                            .w-200
                          v-col(cols="auto")
                            v-chip.chip-headerX(text-color="primaryHover", label, outlined)
                              app-entity-path-selector(
                                :entity="$searchEntity(instance.entity || 'Order')",
                                :title="headerX.path",
                                @field="setHeader(headerX, $event)",
                                :clearable="false",
                                :disabled="disableHeaders",
                                :errors="errors['DataTypeX']",
                                :noMultipleFields="true"
                              )
                              v-btn.ml-1.btn-close(icon='', x-small, color='primary', plain="true", @click="removeHeaderX(index)")
                                v-icon mdi-close
                        v-row.flex-nowrap(dense)
                          v-spacer
                          v-col(cols="auto", v-for="(headerY, index) in headersY", :key="headerY.path")
                            v-chip.chip-headerY(text-color="primaryHover", label, outlined)
                                app-entity-path-selector(
                                  :entity="$searchEntity(instance.entity || 'Order')",
                                  :title="headerY.path",
                                  @field="setHeader(headerY, $event)",
                                  :clearable="false",
                                  :disabled="disableHeaders",
                                  :errors="errors['DataTypeX']",
                                  :noMultipleFields="true"
                                )
                                v-btn.ml-1.btn-close(icon='', x-small, color='primary', plain="true", @click="removeHeaderY(index)")
                                  v-icon mdi-close
                          v-col(cols="auto")
                            v-chip.chip-content(text-color="primaryHover", label, outlined)
                                p.p-content {{ $t('ReferenceMatrix.content') }}
</template>

<style scoped>
  .align-right{
    text-align: right;
  }
  .chips-row {
    display: inline-block;
    width: 200px;
  }
  .chips-rowX {
    padding: 0px 4px 0px 0px;
  }
  .chip-headerX {
    border-top: none;
    border-left: none;
    border-right: none;
    border-radius: 0px !important;
    height: auto;
    border-bottom: solid;
    border-bottom-color: #FF7B8B;
    padding: 4px;
    width: 200px;
    padding: 0px 4px 0px 0px;
  }
  .chip-leftLine {
    border-top: none;
    border-right: none;
    border-radius: 0px !important;
    border-left-style: solid;
    border-bottom: none;
    padding: 0px;
    height: 75px;
  }
  .chip-headerY {
    border-left: none;
    border-right: none;
    border-radius: 0px !important;
    border-top-style: solid;
    height: auto;
    border-bottom: solid;
    border-bottom-color: #F2C94C;
    border-top-width: thin;
    padding: 4px;
  }
  .p-content
  {
    text-align: center;
    margin-bottom: 0px;
    width: 200px;
  }
  .chip-content {
    border-left: none;
    border-right: none;
    border-radius: 0px !important;
    border-top-style: solid;
    height: 81px;
    border-bottom: solid;
    border-bottom-color: #3200E6;
    border-top-width: thin;
    padding: 4px;
    width: 200px;
    color: var(--v-text-base);
    font-weight: 800;
    font-family: "Avenir LT", "Roboto", sans-serif;
    font-size: 13px;
  }
  .btn-close{
    top: -18px;
  }
  .btn-headerX{
    color: white;
    background-color: #FF7B8B !important;
    margin-right: 20px;
  }
  .btn-headerY{
    color: white;
    background-color: #F2C94C !important;
  }
  .grid-container{
    overflow: auto;
  }
  .flex-nowrap {
    flex-wrap: nowrap;
  }
  .w-200 {
    width: 242px;
  }
  .uploading {
    text-align: center;
    color: var(--v-secondary-base) !important;
  }
</style>

<script>
import axios from 'axios'

export default {
  name: 'Reference-Matrix',
  data: () => ({
    loading: true,
    uploading: false,
    instance: {},
    extraFields: [],
    filesToUpload: [],
    filesFormData: null,
    errors: {},
    disableHeaders : true,
    businessScopes: [],
    headersY: [],
    headersX: [],
    firstChange: true
  }),
  computed: {
    entityName () {
      return 'ReferenceMatrix'
    },
    entity () {
      return this.$findEntity('ReferenceMatrix')
    },
    uid () {
      return this.$route.params.item
    },
    ignoreTabs () {
      return ['file', 'businessScope']
    }
  },
  watch: {
    instance: {
      handler(newVal, oldVal) {
        this.$emit('input', newVal);
      },
      deep: true
    },
    'instance.dataTypeContent': function(newVal, oldVal) {
      // Ignore the first change
      if (!this.firstChange) {
        this.instance.defaultValue = '';
      } else {
        this.firstChange = false;
      }
    }
  },
  methods: {
    addHeaderX(){
      this.headersX.push({path:''})
    },
    addHeaderY(){
      this.headersY.push({path:''})
    },
    removeHeaderX(index){
      this.headersX.splice((index), 1);
    },
    removeHeaderY(index){
      this.headersY.splice((index), 1);
    },
    log(val) {
      console.log(this.$cleanString(val));
    },
    initInstance () {
      return {
        ...this.instance,
        user: this.instance && this.instance.id
          ? this.instance.user
          : this.$store.state.user.user
      }
    },
    async getExtraFields (rawValue) {
      this.$store.commit('SET_CRITERIA_ENTITY_NAME', this.instance.entity)
      this.extraFields = (await axios.get(`extraFields?where[]=x => x.entity = "${rawValue}"`)).data.data
      this.disableHeaders = false
      this.cleanHeaders();
    },
    cleanHeaders(){
      this.headersX = [];
      this.headersY = [];
    },
    setHeader(header, { field, path }) {
      header.path = path
    },
    transformInstance(instance) {
      const json = JSON.stringify({...instance, hasFile: !!instance.file });
      const data = new FormData();
      data.append("instance", json);
      data.append("file", instance.file);
      let val = [{file:instance.file}]
      val.map(f => {
        data.append('files', f.file)
      })
      data.append('entity', 'ReferenceMatrix')
      return data;
    },
    // Validate all headers have data
    validateHeaders (){
      if(this.headersX.length == 0) return this.$t('ReferenceMatrix.headerRequired', { axis: 'X' });
      if(this.headersX.filter(h => h.path == "").length > 0) return this.$t('ReferenceMatrix.allHeaderSelected', { axis: 'X' });
      if(this.headersY.length == 0) return this.$t('ReferenceMatrix.headerRequired', { axis: 'Y' });
      if(this.headersY.filter(h => h.path == "").length > 0) return this.$t('ReferenceMatrix.allHeaderSelected', { axis: 'Y' });
      return "";
    },
    ///Concatenate all headers values for each axis
    setHeadersValues(){
      let headerX = "";
      this.headersX.forEach(hx => {
        if(headerX != "") headerX = headerX + ",";
        headerX = headerX + hx.path
      });
      this.instance.headerX = headerX;

      let headerY = "";
      this.headersY.forEach(hy => {
        if(headerY != "") headerY = headerY + ",";
        headerY = headerY + hy.path
      });
      this.instance.headerY = headerY;
    },
    //Load headers X and Y according with headers from database
    loadHeaders(){
      let arrayHeadersX = this.instance.headerX.split(',');
      arrayHeadersX.forEach(hx => {
        this.headersX.push({path:hx})
      });

      let arrayHeadersY = this.instance.headerY.split(',');
      arrayHeadersY.forEach(hy => {
        this.headersY.push({path:hy})
      });
    },
    /* TODO: docs */
    async save () {
      this.uploading = true;
      this.setHeadersValues();
      try {
        if(this.validateHeaders() != "") throw "ErrorHeaders";
        if (this.instance.defaultValue === "" || this.instance.defaultValue === undefined) {
          this.instance.defaultValue = null;
        }
        const _instance = this.transformInstance(this.instance);
        this.instance = (await axios.post('Matrix/setMatrix', 
          _instance,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })).data
        await axios.post(`file/media/${this.instance.id}`, 
          _instance,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
        this.$router.push({
          name: 'entities.index',
          params: { entity: this.entity.name }
        })
      } catch (error) {
        if(error == "ErrorHeaders"){
          let errors = {headers: [this.validateHeaders()]}
          this.$store.commit('SET_ERRORS', errors)
          this.$store.commit('SET_ERROR_MODAL', true)
        }
        else{
          this.errors = error.response.data.errors
        }
      }
      this.uploading = false
    },

    /* TODO: docs */
    async load () {
      if (this.uid) {
        try {
          ({ data: { data: this.businessScopes } } = await axios.get('BusinessScopes', { params: { pageSize: -1 } }));
          this.instance = (await axios.get(`${this.entity.name}/${this.uid}`, {
            _Entity: this.entity.name,
            uid: this.uid,
            params: {
              Load: ['Files','User']
            }
          })).data
          this.loadHeaders();
          this.disableHeaders = false;
        } catch (error) {
          console.error(error)
        }
      }
      this.loading = false
    }
  },
  async mounted () {
    await this.load()
    this.instance = this.initInstance();
  },
}
</script>
